import { cn } from "@/lib/utils";
import type { Scale } from "@andrum/schemas";

interface ScaleSliderProps {
  label: string;
  value: Scale | null;
  onChange: (value: Scale) => void;
  minLabel?: string;
  maxLabel?: string;
  /** Tighter spacing for constrained layouts (e.g. onboarding) */
  compact?: boolean;
}

/**
 * Touch-friendly 0-10 scale with tall hit area and clear value chip.
 */
export function ScaleSlider({
  label,
  value,
  onChange,
  minLabel = "0",
  maxLabel = "10",
  compact = false,
}: ScaleSliderProps) {
  const display = value ?? 5;
  const pct = (display / 10) * 100;

  return (
    <div className={cn(compact ? "space-y-1.5 py-0" : "space-y-3 py-1")}>
      <div className="flex items-center justify-between gap-3">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <span
          className={cn(
            "min-w-10 rounded-full px-2.5 py-1 text-center text-sm font-semibold tabular-nums",
            value == null
              ? "bg-surface-muted text-foreground-subtle"
              : "bg-primary-soft text-primary"
          )}
          aria-live="polite"
        >
          {value ?? "·"}
        </span>
      </div>

      <div className={cn("relative px-0.5", compact ? "py-1.5" : "py-3")}>
        {/* Track background */}
        <div
          className="pointer-events-none absolute left-0 right-0 top-1/2 h-2.5 -translate-y-1/2 rounded-full bg-surface-muted"
          aria-hidden
        />
        {/* Filled portion */}
        <div
          className="pointer-events-none absolute left-0 top-1/2 h-2.5 -translate-y-1/2 rounded-full bg-primary/70"
          style={{ width: `${pct}%` }}
          aria-hidden
        />
        <input
          type="range"
          min={0}
          max={10}
          step={1}
          value={display}
          onChange={(e) => onChange(Number(e.target.value) as Scale)}
          className={cn(
            "andrum-range relative z-10 h-12 w-full cursor-pointer appearance-none bg-transparent"
          )}
          aria-valuemin={0}
          aria-valuemax={10}
          aria-valuenow={value ?? undefined}
          aria-label={label}
        />
      </div>

      <div className="flex justify-between px-0.5 text-xs text-foreground-subtle">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
}
